import { useMemo, useState } from "react";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { DataTable } from "@repo/ui/components/Base/DataTable";
import { layoutDefaults, useColumns } from "./AgendaModulesDataTableColumns";
import { EditAgendaModuleSheet } from "./EditAgendaModuleSheet";
import { ArchiveConfirmation } from "../dialog/ArchiveConfirmation";
import { useUserColumns } from "@/hooks/useUserColumns";
import { KEYS } from "@/lib/constants";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { useNavigate } from "react-router-dom";
import { AgendaModuleCopyDialog } from "./AgendaModuleCopyDialog";
import { agendaModuleRepo } from "@/repositories/agendaModuleRepo";
import { useAgendaModuleList } from "@/hooks/queryHooks/agendaModuleHooks";
import { useActiveTeamIdOrOrg } from "@/hooks/useActiveTeam";

export const AgendaModulesTable = ({ tenantId }: { tenantId: string }) => {
  const navigate = useNavigate();
  const { data } = useAgendaModuleList(tenantId, useActiveTeamIdOrOrg() ?? "");
  const agendaModules = data?.filter((m) => m.template);

  const { defaultState, saveTableLayout, resetTableLayout } = useUserColumns(
    KEYS.AGENDAMODULE_TABLE_COLUMNS,
    layoutDefaults
  );

  const [activeAgendaModule, setActiveAgendaModule] = useState<string>();
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [isCopyFormOpen, setIsCopyFormOpen] = useState(false);

  const template = useMemo(
    () => data?.find((d) => d.id === activeAgendaModule),
    [data, activeAgendaModule]
  );

  const handleQuickEdit = (id: string) => {
    setEditSheetOpen(true);
    setActiveAgendaModule(id);
  };

  const handleEdit = (id: string) => {
    setEditSheetOpen(true);
    setActiveAgendaModule(id);
  };

  const handleArchive = (docId: string) => {
    setActiveAgendaModule(docId);
    setArchiveOpen(true);
  };

  const handleView = (id: string) => {
    navigate(new AppUrlPath(tenantId).getAgendaModulePath(`${id}`));
  };

  const handleCopy = (id: string) => {
    setIsCopyFormOpen(true);
    setActiveAgendaModule(id);
  };

  return (
    <>
      {template && (
        <AgendaModuleCopyDialog
          open={isCopyFormOpen}
          setOpen={setIsCopyFormOpen}
          template={template}
          tenantId={tenantId}
        />
      )}

      {activeAgendaModule && (
        <ArchiveConfirmation
          open={archiveOpen}
          setOpen={setArchiveOpen}
          itemId={activeAgendaModule}
          onSubmit={async (agendaModuleId) => {
            await agendaModuleRepo.delete(tenantId, agendaModuleId);
          }}
        />
      )}

      {!agendaModules && <Spinner />}

      {activeAgendaModule && (
        <EditAgendaModuleSheet
          open={editSheetOpen}
          setOpen={setEditSheetOpen}
          tenantId={tenantId}
          agendaModuleId={activeAgendaModule}
        />
      )}

      <DataTable
        columns={useColumns({
          tenantId,
          handleQuickEdit,
          handleEdit,
          handleArchive,
          handleView,
          handleCopy,
        })}
        data={agendaModules ?? []}
        includeMainFilter={false}
        userDefaults={defaultState}
        factoryDefaults={layoutDefaults}
        onLayoutSave={saveTableLayout}
        onLayoutReset={resetTableLayout}
      />
    </>
  );
};
